import { create } from 'zustand'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getFilterAPI, updateFilterAPI, resetFilterAPI, createPresetAPI, deletePresetAPI, applyPresetAPI, updatePresetAPI } from '../lib/api/api'

// Exported so other hooks (e.g. useColumnSettingsSync) can access preset state
export let useFilterStore = create((set, get) => ({
    selectedPresetId: null,
    setSelectedPresetId: (presetId) => set({ selectedPresetId: presetId }),

    // true when the user changed rows while a preset is active
    rowsEdited: false,
    setRowsEdited: (val) => set({ rowsEdited: val }),

    // bumped after apply_preset so useColumnSettingsLoader re-fetches
    columnSettingsVersion: 0,
    bumpColumnSettingsVersion: () => set(s => ({ columnSettingsVersion: s.columnSettingsVersion + 1 })),

    // suppress rowsEdited flag for ms after a preset's rows are applied
    rowsEditedBlockedUntil: 0,
    blockRowsEdited: (ms = 2000) => set({ rowsEditedBlockedUntil: Date.now() + ms }),

    // Module mit offenem Filter-Panel: ProductGrid/ProductTable halten ihre
    // Daten-Query und den pageIndex-Reset an, bis das Panel schliesst --
    // Filter-Edits im Panel sollen die Produktliste nicht live umladen.
    openFilterPanels: {},
    setFilterPanelOpen: (application, open) => set((s) => ({
        openFilterPanels: { ...s.openFilterPanels, [application]: !!open },
    })),
}))


export let useFiltersQuery = (application) => useQuery({
    queryKey: ['filters', application],
    queryFn: () => getFilterAPI(application),
    staleTime: 60000,
})


// Optimistisches Update von current_filter im Cache: Pills/Modal reagieren sofort und
// abhaengige Logik (z.B. der pageIndex-Reset in ProductGrid/ProductTable) feuert, ohne
// auf POST + Filter-Refetch zu warten. Server-Semantik fuer Array-Attribute (multi):
// Wert togglen; fuer Skalare: setzen. Der invalidate danach reconciled mit dem Server.
let applyFilterToCache = (queryClient, application, { filter_id, filter_type, attribute }) => {
    queryClient.setQueryData(['filters', application], (old) => {
        if (!old?.current_filter) return old
        const def = old.filter?.advanced_filter?.find(d => d.filter_id === filter_id)
        const isMulti = def?.input_type === 'dropdown_multiple' || def?.input_type === 'input_list'
        const existing = old.current_filter.find(f => f.filter_id === filter_id && f.filter_type === filter_type)

        let nextFilters
        if (!existing) {
            nextFilters = [...old.current_filter, { filter_id, filter_type, attribute: isMulti ? [attribute] : attribute }]
        } else {
            nextFilters = old.current_filter
                .map(f => {
                    if (f !== existing) return f
                    if (Array.isArray(f.attribute)) {
                        const has = f.attribute.includes(attribute)
                        return { ...f, attribute: has ? f.attribute.filter(a => a !== attribute) : [...f.attribute, attribute] }
                    }
                    return { ...f, attribute }
                })
                .filter(f => !(Array.isArray(f.attribute) && f.attribute.length === 0))
        }
        return { ...old, current_filter: nextFilters }
    })
}

let removeFilterFromCache = (queryClient, application, filter_id, filter_type) => {
    queryClient.setQueryData(['filters', application], (old) => {
        if (!old?.current_filter) return old
        if (!filter_id) {
            return { ...old, current_filter: [] }
        }
        return {
            ...old,
            current_filter: old.current_filter.filter(f =>
                !(f.filter_id === filter_id && (filter_type === undefined || filter_type === null || f.filter_type === filter_type))
            ),
        }
    })
}

// Gemeinsamer mutationKey fuer alle Filter-Schreiboperationen. Damit laesst sich
// pruefen, ob parallel noch weitere Writes laufen: der Reconcile-GET (und der
// Daten-Reload) feuert nur beim LETZTEN noch laufenden Write. Sonst gilt bei
// schnellen aufeinanderfolgenden Aenderungen (z.B. mehrere Pills loeschen): der
// Reconcile-Snapshot des ersten Writes kennt die spaeteren Loeschungen noch nicht,
// ueberschreibt die optimistische UI und geloeschte Pills ploppen kurz wieder auf.
//
// Drittes Key-Element 'data' | 'silent': ob der Write nach Abschluss einen
// Daten-Reload ausloest (Pills) oder nicht (Modal-Edits, Reload erst bei Close).
// ProductGrid/ProductTable pausieren ihre Daten-Query, solange ueberhaupt Writes
// laufen (Prefix-Match), und zeigen Skeleton nur bei laufenden 'data'-Writes.
let filterWriteKey = (application) => ['filter-write', application]

let isLastFilterWrite = (queryClient, application) =>
    queryClient.isMutating({ mutationKey: filterWriteKey(application) }) <= 1

// Daten-Invalidate bewusst auch im Fehlerfall des eigenen POSTs: parallel gelaufene
// Writes koennen erfolgreich gewesen sein -- der Refetch holt den echten Serverstand.
let reconcileIfLastWrite = (queryClient, application, updateData) => {
    if (!isLastFilterWrite(queryClient, application)) return
    queryClient.invalidateQueries({ queryKey: ['filters', application] })
    if (updateData) {
        queryClient.invalidateQueries({ queryKey: ['data', application] })
    }
}


export let useUpdateFilterMutation = (application, updateData = true) => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationKey: [...filterWriteKey(application), updateData ? 'data' : 'silent'],
        mutationFn: async (data) => {
            // Laufende Filter-GETs abbrechen, damit deren (aelterer) Snapshot nicht
            // gleich das optimistische Update ueberschreibt
            await queryClient.cancelQueries({ queryKey: ['filters', application] })
            applyFilterToCache(queryClient, application, data)
            try {
                await updateFilterAPI(application, data.filter_id, data.filter_type, data.attribute)
            } finally {
                // reconcile mit Serverstand -- im Fehlerfall ist das zugleich der Rollback
                reconcileIfLastWrite(queryClient, application, updateData)
            }
        },
    })
}


export let useResetFilterMutation = (application, updateData = true) => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationKey: [...filterWriteKey(application), updateData ? 'data' : 'silent'],
        mutationFn: async (data) => {
            await queryClient.cancelQueries({ queryKey: ['filters', application] })
            removeFilterFromCache(queryClient, application, data?.filter_id, data?.filter_type)
            try {
                await resetFilterAPI(application, data?.filter_id, data?.filter_type)
            } finally {
                reconcileIfLastWrite(queryClient, application, updateData)
            }
        },
    })
}


export let useUpdatePresetMutation = (application, updateData = true) => {
    let queryClient = useQueryClient()
    let setRowsEdited = useFilterStore(state => state.setRowsEdited)
    return useMutation({
        mutationKey: [...filterWriteKey(application), updateData ? 'data' : 'silent'],
        mutationFn: async (preset_id) => {
            await queryClient.cancelQueries({ queryKey: ['filters', application] })
            // Snapshot optimistisch auf den aktuellen Filterstand setzen: isEdited
            // kippt sofort auf false statt erst nach dem Reconcile-GET -- der
            // "Update Preset"-Button flackerte sonst (Saving -> Update -> weg).
            queryClient.setQueryData(['filters', application], (old) => {
                const preset = old?.current_presets?.[String(preset_id)]
                if (!preset) return old
                return {
                    ...old,
                    current_presets: {
                        ...old.current_presets,
                        [String(preset_id)]: [preset[0], ...(old.current_filter || [])],
                    },
                }
            })
            try {
                await updatePresetAPI(application, preset_id)
                setRowsEdited(false)
            } finally {
                reconcileIfLastWrite(queryClient, application, updateData)
            }
        },
    })
}


export let useCreatePresetMutation = (application) => {
    let queryClient = useQueryClient()
    let setRowsEdited = useFilterStore(state => state.setRowsEdited)
    return useMutation({
        mutationKey: [...filterWriteKey(application), 'silent'],
        mutationFn: async (data) => {
            try {
                const result = await createPresetAPI(application, data.preset_name)
                setRowsEdited(false)
                return result
            } finally {
                reconcileIfLastWrite(queryClient, application, false)
            }
        },
    })
}


// Preset optimistisch aus dem Cache nehmen: das Dropdown reagiert sofort, der
// Reconcile holt bei einem fehlgeschlagenen Delete den Serverstand zurueck.
let removePresetFromCache = (queryClient, application, preset_id) => {
    queryClient.setQueryData(['filters', application], (old) => {
        if (!old?.current_presets) return old
        const key = String(preset_id)
        if (!(key in old.current_presets)) return old
        return {
            ...old,
            current_presets: Object.fromEntries(
                Object.entries(old.current_presets).filter(([id]) => id !== key)
            ),
        }
    })
}

export let useDeletePresetMutation = (application) => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationKey: [...filterWriteKey(application), 'silent'],
        mutationFn: async (preset_id) => {
            await queryClient.cancelQueries({ queryKey: ['filters', application] })
            removePresetFromCache(queryClient, application, preset_id)
            try {
                await deletePresetAPI(application, preset_id)
            } finally {
                reconcileIfLastWrite(queryClient, application, false)
            }
        },
    })
}


export let useApplyPresetMutation = (application, updateData = true) => {
    let queryClient = useQueryClient()
    let setRowsEdited = useFilterStore(state => state.setRowsEdited)
    let bumpColumnSettingsVersion = useFilterStore(state => state.bumpColumnSettingsVersion)
    let blockRowsEdited = useFilterStore(state => state.blockRowsEdited)
    return useMutation({
        mutationKey: [...filterWriteKey(application), updateData ? 'data' : 'silent'],
        mutationFn: async (preset_id) => {
            await queryClient.cancelQueries({ queryKey: ['filters', application] })
            queryClient.setQueryData(['filters', application], (data) => {
                // Guard: Preset kann im Cache fehlen (leerer Cache, gerade geloescht)
                // -> nichts patchen statt crashen; der Reconcile holt den Stand.
                const preset = data?.current_presets?.[String(preset_id)]
                if (!preset) return data
                return {
                    ...data,
                    current_filter: preset.slice(1),
                }
            })
            try {
                let response = await applyPresetAPI(application, preset_id)

                setRowsEdited(false)
                if (response?.rows) {
                    // Block rowsEdited for 2s so the loader re-apply doesn't trigger "update?"
                    blockRowsEdited(2000)
                    bumpColumnSettingsVersion()
                }
            } finally {
                reconcileIfLastWrite(queryClient, application, updateData)
            }
        },
    })
}


let presetDigest = (filters) => filters
    ?.map(filter => `${filter.filter_id}-${filter.filter_type}-${
        Array.isArray(filter.attribute) ?
            filter.attribute.sort().join(',') :
            filter.attribute
    }`)
    .sort()
    .join(';')

export let useSelectedPreset = (application) => {
    // TODO parametrize store by application
    let { data } = useFiltersQuery(application)

    let selectedPresetId = useFilterStore(state => state.selectedPresetId)
    let setSelectedPresetId = useFilterStore(state => state.setSelectedPresetId)
    let rowsEdited = useFilterStore(state => state.rowsEdited)

    let isFiltersEdited = presetDigest(data?.current_filter || []) !== presetDigest(data?.current_presets?.[selectedPresetId]?.slice(1) || [])
    let isEdited = isFiltersEdited || (!!selectedPresetId && rowsEdited)

    return { selectedPresetId, setSelectedPresetId, isEdited }
}